import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  Divider,
  Badge,
  useBreakpointValue,
  InputGroup,
  InputLeftElement,
  Checkbox
} from '@chakra-ui/react';
import { EmailIcon, PhoneIcon, InfoIcon, CalendarIcon, TimeIcon } from '@chakra-ui/icons';

import { bookAppointment } from 'services/appointmentBooking';

function BookingForm({
  student,
  selectedSlot,
  teacherName,
  onBookingSubmitted,
  loading,
  setLoading,
  error,
  setError
}) {
  const [formData, setFormData] = useState({
    guardian_name: '',
    guardian_email: '',
    guardian_phone: '',
    subject: '',
    notes: '',
    confirm_data: false,
    accept_terms: false
  });

  const [formErrors, setFormErrors] = useState({});

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Manejar cambios en el formulario
  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error del campo
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    setError('');
  };

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    // Validar nombre del apoderado
    if (!formData.guardian_name.trim()) {
      errors.guardian_name = 'Nombre del apoderado es requerido';
    } else if (formData.guardian_name.trim().length < 3) {
      errors.guardian_name = 'Nombre debe tener al menos 3 caracteres';
    }

    // Validar email
    if (!formData.guardian_email.trim()) {
      errors.guardian_email = 'Email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guardian_email)) {
      errors.guardian_email = 'Email no es válido';
    }

    // Validar asunto (obligatorio)
    if (!formData.subject.trim()) {
      errors.subject = 'El asunto de la reunión es requerido';
    } else if (formData.subject.trim().length < 5) {
      errors.subject = 'El asunto debe tener al menos 5 caracteres';
    }

    // Validar teléfono (opcional pero si se ingresa debe ser válido)
    if (formData.guardian_phone.trim() && !/^[\d\s\-\+\(\)]+$/.test(formData.guardian_phone)) {
      errors.guardian_phone = 'Teléfono no es válido';
    }

    // Validar checkboxes
    if (!formData.confirm_data) {
      errors.confirm_data = 'Debe confirmar que los datos son correctos';
    }

    if (!formData.accept_terms) {
      errors.accept_terms = 'Debe aceptar los términos y condiciones';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const appointmentData = {
        student_rut: student.username, // El username es el RUT
        teacher_id: selectedSlot.teacher_id,
        appointment_date: selectedSlot.date,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        guardian_name: formData.guardian_name.trim(),
        guardian_email: formData.guardian_email.trim(),
        guardian_phone: formData.guardian_phone.trim() || null,
        subject: formData.subject.trim(),
        notes: formData.notes.trim() || null
      };

      const response = await bookAppointment(appointmentData);
      
      if (response.success) {
        onBookingSubmitted({
          ...response.data,
          slot: selectedSlot,
          guardian: {
            name: formData.guardian_name,
            email: formData.guardian_email,
            phone: formData.guardian_phone
          }
        });
      } else {
        setError(response.message || 'No se pudo completar la reserva');
      }
    } catch (err) {
      console.error('Error booking appointment:', err);
      setError(err.message || 'Error al reservar la cita. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch" maxW="600px" mx="auto">
      <Box textAlign="center">
        <Text 
          fontSize={isMobile ? 'lg' : 'xl'} 
          fontWeight="bold" 
          color={textColor}
          mb={2}
        >
          Completar Datos del Apoderado
        </Text>
        <Text 
          fontSize={isMobile ? 'sm' : 'md'} 
          color="gray.600"
        >
          Ingrese sus datos para confirmar la reserva de la cita
        </Text>
      </Box>

      {/* Resumen de la cita */}
      <Box border="1px solid" borderColor={borderColor} borderRadius="md" bg={cardBg}>
        <Box px={4} pt={4} pb={2}>
          <Text fontWeight="bold" fontSize="md">
            Resumen de la Cita
          </Text>
        </Box>

        <Box px={4} pb={4} pt={0}>
          <VStack spacing={3} align="start">
            <HStack spacing={3}>
              <InfoIcon color="blue.500" />
              <VStack spacing={0} align="start">
                <Text fontSize="sm" fontWeight="medium">Estudiante:</Text>
                <Text fontSize="sm">{student.display_name}</Text>
                {student.formatted_rut && (
                  <Text fontSize="xs" color="gray.500">RUT: {student.formatted_rut}</Text>
                )}
                {student.group_name && (
                  <Badge colorScheme="blue" fontSize="xs" mt="2px">{student.group_name}</Badge>
                )}
              </VStack>
            </HStack>
            
            <HStack spacing={3}>
              <CalendarIcon color="blue.500" />
              <VStack spacing={0} align="start">
                <Text fontSize="sm" fontWeight="medium">Fecha y hora:</Text>
                <Text fontSize="sm">
                  {selectedSlot.formatted_date}
                </Text>
                <Text fontSize="sm">
                  {selectedSlot.formatted_time}
                </Text>
              </VStack>
            </HStack>
            
            <HStack spacing={3}>
              <InfoIcon color="blue.500" />
              <VStack spacing={0} align="start">
                <Text fontSize="sm" fontWeight="medium">Profesor:</Text>
                <Text fontSize="sm">{teacherName || selectedSlot.teacher_name}</Text>
              </VStack>
            </HStack>
          </VStack>
        </Box>
      </Box>

      {/* Formulario */}
      <Box border="1px solid" borderColor={borderColor} borderRadius="md" bg={cardBg}>
        <Box p={4}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={5}>
              <FormControl isInvalid={!!formErrors.guardian_name} isRequired>
                <FormLabel fontSize="sm" color={textColor}>
                  Nombre Completo del Apoderado
                </FormLabel>
                <Input
                  value={formData.guardian_name}
                  onChange={handleInputChange('guardian_name')}
                  placeholder="Ej: María González Pérez"
                  size={isMobile ? 'md' : 'lg'}
                  borderColor={borderColor}
                  isDisabled={loading}
                />
                <FormErrorMessage>{formErrors.guardian_name}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!formErrors.guardian_email} isRequired>
                <FormLabel fontSize="sm" color={textColor}>
                  Email de Contacto
                </FormLabel>
                <InputGroup size={isMobile ? 'md' : 'lg'}>
                  <InputLeftElement pointerEvents="none">
                    <EmailIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    type="email"
                    value={formData.guardian_email}
                    onChange={handleInputChange('guardian_email')}
                    placeholder="ejemplo@correo.com"
                    borderColor={borderColor}
                    isDisabled={loading}
                  />
                </InputGroup>
                <FormErrorMessage>{formErrors.guardian_email}</FormErrorMessage>
                <FormHelperText fontSize="xs">
                  Se enviará la confirmación a este email
                </FormHelperText>
              </FormControl>

              <FormControl isInvalid={!!formErrors.guardian_phone}>
                <FormLabel fontSize="sm" color={textColor}>
                  Teléfono de Contacto (Opcional)
                </FormLabel>
                <InputGroup size={isMobile ? 'md' : 'lg'}>
                  <InputLeftElement pointerEvents="none">
                    <PhoneIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    type="tel"
                    value={formData.guardian_phone}
                    onChange={handleInputChange('guardian_phone')}
                    placeholder="+56 9 1234 5678"
                    borderColor={borderColor}
                    isDisabled={loading}
                  />
                </InputGroup>
                <FormErrorMessage>{formErrors.guardian_phone}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!formErrors.subject} isRequired>
                <FormLabel fontSize="sm" color={textColor}>
                  Asunto de la Reunión
                </FormLabel>
                <Input
                  value={formData.subject}
                  onChange={handleInputChange('subject')}
                  placeholder="Ej: Rendimiento académico, conducta, situación familiar..."
                  size={isMobile ? 'md' : 'lg'}
                  borderColor={borderColor}
                  isDisabled={loading}
                />
                <FormErrorMessage>{formErrors.subject}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" color={textColor}>
                  Notas Adicionales (Opcional)
                </FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={handleInputChange('notes')}
                  placeholder="Información adicional relevante para la reunión..."
                  rows={3}
                  borderColor={borderColor}
                  isDisabled={loading}
                  resize="vertical"
                />
                <FormHelperText fontSize="xs">
                  Máximo 500 caracteres
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Checkboxes de confirmación */}
              <VStack spacing={3} align="start" w="full">
                <FormControl isInvalid={!!formErrors.confirm_data}>
                  <Checkbox
                    isChecked={formData.confirm_data}
                    onChange={handleInputChange('confirm_data')}
                    isDisabled={loading}
                    size="sm"
                  >
                    <Text fontSize="sm">
                      Confirmo que todos los datos ingresados son correctos
                    </Text>
                  </Checkbox>
                  <FormErrorMessage>{formErrors.confirm_data}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!formErrors.accept_terms}>
                  <Checkbox
                    isChecked={formData.accept_terms}
                    onChange={handleInputChange('accept_terms')}
                    isDisabled={loading}
                    size="sm"
                  >
                    <Text fontSize="sm">
                      Acepto los términos y condiciones del sistema de reservas
                    </Text>
                  </Checkbox>
                  <FormErrorMessage>{formErrors.accept_terms}</FormErrorMessage>
                </FormControl>
              </VStack>

              {/* Notificaciones que se enviarán */}
              <Box w="full" border="1px solid" borderColor="green.200" borderRadius="md" bg="green.50" px={3} py={2}>
                <Text fontSize="xs" fontWeight="700" color="green.700" mb={2}>
                  Recibirá la información de su reserva en:
                </Text>
                <VStack spacing={1} align="start">
                  <HStack spacing={2}>
                    <EmailIcon color="green.500" w="12px" h="12px" />
                    <Text fontSize="xs" color="green.800">
                      <strong>Apoderado:</strong>{' '}
                      {formData.guardian_email.trim() || <em style={{ color: '#999' }}>ingrese su email arriba</em>}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                w="full"
                isLoading={loading}
                loadingText="Reservando..."
                isDisabled={loading}
              >
                Confirmar Reserva
              </Button>
            </VStack>
          </form>
        </Box>
      </Box>

      {/* Información importante */}
      <Box border="1px solid" borderColor="blue.200" borderRadius="md" bg="blue.50">
        <Box px={4} py={3}>
          <VStack spacing={2} align="start">
            <HStack spacing={2}>
              <InfoIcon color="blue.500" w="4" h="4" />
              <Text fontSize="sm" fontWeight="medium" color="blue.700">
                Información importante:
              </Text>
            </HStack>
            
            <VStack spacing={1} align="start" fontSize="xs" color="blue.600" pl={6}>
              <Text>• Su solicitud quedará pendiente hasta que el profesor la confirme</Text>
              <Text>• Recibirá un email al registrar la solicitud y otro cuando el profesor la confirme</Text>
              <Text>• Si necesita cancelar, contacte directamente al establecimiento</Text>
              <Text>• Por favor llegue 5 minutos antes de la hora agendada</Text>
            </VStack>
          </VStack>
        </Box>
      </Box>
    </VStack>
  );
}

export default BookingForm;